import { updateLlmGatewayConfiguration } from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { supportUrl } from "@app/routes";
import type { DeployLlmGatewayConfiguration } from "@app/types";
import { BannerMessages } from "@app/ui/shared/banner";
import { Button } from "@app/ui/shared/button";
import { FormGroup } from "@app/ui/shared/form-group";
import { Input } from "@app/ui/shared/input";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { IconSettings } from "../icons";

interface EditAiGatewaySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: DeployLlmGatewayConfiguration;
}

export const EditAiGatewaySettingsModal = ({
  isOpen,
  onClose,
  config,
}: EditAiGatewaySettingsModalProps) => {
  const dispatch = useDispatch();
  const [spendLimit, setSpendLimit] = useState<number | undefined>(
    config.spendLimit,
  );
  const orgId = useSelector(selectOrganizationSelectedId);
  const [spendLimitError, setSpendLimitError] = useState<string | undefined>();
  const loader = useLoader(updateLlmGatewayConfiguration);

  useLoaderSuccess(loader, onClose);

  useEffect(() => {
    if (isOpen) return;
    setSpendLimit(config.spendLimit);
    setSpendLimitError(undefined);
  }, [isOpen, config.spendLimit]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!spendLimit) {
      setSpendLimitError("Spend limit is required.");
      return;
    }

    if (spendLimit > 10_000) {
      setSpendLimitError("Spend limit cannot exceed $10,000.");
      return;
    }

    setSpendLimitError(undefined);
    dispatch(
      updateLlmGatewayConfiguration({ orgId, id: config.id, spendLimit }),
    );
  };

  if (!isOpen) return null;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={onClose} className="min-w-[600px]">
        <ModalHeading
          icon={<IconSettings variant="sm" />}
          title="LLM Gateway Settings"
          onClose={onClose}
        />
        <ModalBody>
          <form id="edit-gateway-settings" noValidate onSubmit={onSubmit}>
            <FormGroup
              label="Monthly Spend Limit (USD)"
              htmlFor="spend-limit"
              feedbackMessage={
                spendLimitError ? (
                  <>
                    {spendLimitError} Need help?{" "}
                    <Link to={supportUrl()}>Contact support</Link>
                  </>
                ) : null
              }
              feedbackVariant={spendLimitError ? "danger" : "default"}
            >
              <Input
                id="spend-limit"
                type="number"
                min={0}
                max={10_000}
                value={spendLimit}
                onChange={(e) =>
                  setSpendLimit(
                    e.target.value
                      ? Number.parseFloat(e.target.value)
                      : undefined,
                  )
                }
                disabled={config.trial}
              />
            </FormGroup>
            <BannerMessages className="mt-4" {...loader} />
          </form>
        </ModalBody>
        <ModalFooter
          actions={[
            <Button key="cancel" variant="white" onClick={onClose}>
              Cancel
            </Button>,
            <Button
              key="save"
              type="submit"
              form="edit-gateway-settings"
              isLoading={loader.isLoading}
              disabled={config.trial}
            >
              Save
            </Button>,
          ]}
        />
      </Modal>
    </ModalWrapper>
  );
};
